import * as posMenuEndpoints from "../../lib/api/menus";
import * as posStoreEndpoints from "../../lib/api/stores";
import * as posActions from "../../lib/posActions";
import * as printers from "../../lib/api/printers";
import * as routeHelpers from "../../lib/routeHelpers";
// import * as posTillEndpoints from "../../lib/api/till";
import {Actions, API, Constants} from "habit-core";
// import {CashPullContext} from "../CashPullContext";
import completeIcon from "../../images/complete.svg";
import describeNetworkError from "../../lib/describeNetworkError";
import ErrorModal from "../ErrorModal";
import logger from "../../lib/sentryLogger";
import OutlineButton from "../OutlineButton";
import React, {useContext, useEffect, useState} from "react";
import {RefreshContext} from "../RefreshContext";
import {StationContext} from "../StationContext";
import styles from "./OrderComplete.module.css";
// import {TillAssignmentContext} from "../TillAssignmentContext";
import {useAppDispatch, useAppSelector} from "../../lib/hooks";
import {useLocation, useNavigate} from "react-router-dom";
import classNames from "classnames";
import TextModal from "../TextModal";

const strings = {
    orderComplete: (orderNumber: number) => `Order #${orderNumber} Completed`,
    orderNotSentError: "Order Not Sent to Kitchen",
    orderNotSentInstructions:
        "Payment for this order was captured, but this order was not successfully sent to the kitchen. Please give the printed receipt to the kitchen, so this order can be completed.",
    startNewOrder: "Start a New Order",
    returnToOrders: "Return to Orders",
    printGuestReceipt: "Print Guest Receipt",
    requestSent: "Request Sent",
    confirmPrinted:
        "Your request to print this order's receipt has been submitted",
    ok: "Ok",
};

type OrderCompleteState = {
    header: string;
    subheader?: string;
    subheaderClassName?: string;
    instructions?: string;
    button?: string;
};

export default function OrderComplete() {
    const dispatch = useAppDispatch();
    const {isTraining} = useContext(StationContext);
    const storeId = useAppSelector((state) => state.currentOrder.storeId);
    const defaultCategoryId = useAppSelector(
        (state) =>
            state.menus.byId[state.currentOrder.menuId ?? ""].defaultCategoryId,
    );
    const orderNumber = useAppSelector(
        (state) => state.pos.currentOrder.orderNumber,
    ); // order number should not be null when we arrive here after customer paid for order.
    const orderId = useAppSelector((state) => state.pos.currentOrder.id);
    const orderQsrError = orderId
        ? useAppSelector((state) => state.pos.orders.byId[orderId]?.qsrError)
        : null;
    const stationMode = useAppSelector((state) => state.pos.station.mode);
    const isDriveThruFulfillment =
        stationMode === "drive_thru_order_fulfillment";

    const autoPrintReceipts = useAppSelector(
        (state) => state.pos.station.autoPrintReceipts,
    );

    const navigate = useNavigate();
    const location = useLocation();
    const locationState = location.state as OrderCompleteState;

    const [showQsrError, setShowQsrError] = useState(!!orderQsrError);

    const {
        globalMenuLastUpdateDateRef,
        storeMenuLastUpdateDateRef,
        storeSettingsLastUpdateDateRef,
    } = useContext(RefreshContext);

    // const {assignedTill} = useContext(TillAssignmentContext);
    // const {
    //     setCashPullAlertVisible,
    //     setCurrentAmountCents,
    //     setSuggestedPullAmountCents,
    //     setMaxPullAmountCents,
    //     skipCurrentAmountCheck,
    // } = useContext(CashPullContext);

    useEffect(() => {
        return () => {
            let nextOrderType: API.models.OrderType = "in_store";
            if (
                stationMode === "drive_thru_order_taker" ||
                stationMode === "drive_thru_order_fulfillment"
            ) {
                nextOrderType = "drive_thru";
            }
            dispatch(
                posActions.clearCurrentOrder(storeId ?? "", nextOrderType),
            );
            if (!isDriveThruFulfillment) {
                dispatch(posActions.reserveCurrentOrderId(isTraining));
            }

            // TODO: skip for drive thru order taker

            // if (!isTraining) {
            //     if (!skipCurrentAmountCheck) {
            //         posTillEndpoints
            //             .getCurrentAmount(assignedTill)
            //             .then(
            //                 ({
            //                     currentAmountCents,
            //                     suggestedPullAmountCents,
            //                     maxPullAmountCents,
            //                 }) => {
            //                     setCurrentAmountCents(currentAmountCents);
            //                     if (
            //                         suggestedPullAmountCents !== null &&
            //                         suggestedPullAmountCents > 0
            //                     ) {
            //                         setSuggestedPullAmountCents(
            //                             suggestedPullAmountCents,
            //                         );
            //                         setMaxPullAmountCents(
            //                             maxPullAmountCents ?? 0,
            //                         ); // this shouldn't be null if suggestedPullAmountCents is not null
            //                         setCashPullAlertVisible(true);
            //                     }
            //                 },
            //             )
            //             .catch(); // silently discard
            //     } else {
            //         setCashPullAlertVisible(true);
            //     }
            // }

            Promise.all([
                posMenuEndpoints.getGlobalMenuLastUpdateDate(),
                posMenuEndpoints.getStoreMenuLastUpdateDate(storeId ?? ""),
                posStoreEndpoints.getStoreSettingsLastUpdateDate(),
            ])
                .then(
                    async ([
                        _globalMenuLastUpdateDate,
                        _storeMenuLastUpdateDate,
                        _storeSettingsLastUpdateDate,
                    ]) => {
                        if (
                            globalMenuLastUpdateDateRef.current === null ||
                            globalMenuLastUpdateDateRef.current <
                                _globalMenuLastUpdateDate
                        ) {
                            await dispatch(
                                Actions.menuActions.loadGlobalMenu(),
                            );
                            await dispatch(posActions.initializePosAddOns());
                            globalMenuLastUpdateDateRef.current =
                                _globalMenuLastUpdateDate;
                        }
                        if (
                            storeMenuLastUpdateDateRef.current === null ||
                            storeMenuLastUpdateDateRef.current <
                                _storeMenuLastUpdateDate
                        ) {
                            await dispatch(
                                posActions.refreshStoreMenu(
                                    storeId ?? "",
                                    nextOrderType,
                                ),
                            );
                            await dispatch(
                                posActions.initializePosAllPreps(storeId ?? ""),
                            );
                            storeMenuLastUpdateDateRef.current =
                                _storeMenuLastUpdateDate;
                        }
                        if (
                            storeSettingsLastUpdateDateRef.current === null ||
                            storeSettingsLastUpdateDateRef.current <
                                _storeSettingsLastUpdateDate
                        ) {
                            const storeSettings =
                                await posStoreEndpoints.getStoreSettings();
                            const charitySettings =
                                storeSettings.charitySettings;
                            await dispatch(
                                posActions.initializeCharityRoundUpSettingsImmediate(
                                    charitySettings.isEnabled,
                                    charitySettings.mode,
                                    charitySettings.charityName,
                                    charitySettings.prompt,
                                    charitySettings.rewardMinimumCents,
                                    charitySettings.rewardMessage,
                                ),
                            );
                            dispatch(
                                posActions.setSupportedExternalTenders(
                                    storeSettings.externalTenders ?? [],
                                ),
                            );

                            // initialize store address and tax rate.
                            const storeInfo = {
                                id: storeSettings.storeId,
                                taxRate: storeSettings.taxRate,
                                address: storeSettings.address,
                                established: "n/a",
                                name: "n/a",
                                status: Constants.storeStatus.ONLINE,
                                storeNumber: 0,
                                maxOrderCost: 500,
                                maxOrderCostCents: 50000,
                                supportsCurbside: false,
                                supportsCharlane: false,
                                curbsideAutoCheckIn: {enabled: false},
                                supportsDineIn: true,
                                hours: {
                                    driveThru: [],
                                    inStore: [],
                                    modified: {
                                        driveThru: [],
                                        inStore: [],
                                    },
                                },
                                images: [],
                                supportsQrToGo: false,
                                supportsQrDineIn: false,
                                isItemComboUpsellEnabled: false,
                                supportsPickupLane: false,
                                maxGiftCardCashOutCents:
                                    storeSettings.maxGiftCardCashOutCents,
                                fundraiserSupportEnabled: false,
                                isCondimentUpsellEnabled: false,
                            };
                            await dispatch(
                                Actions.storeActions.setStores([storeInfo]),
                            );
                            storeSettingsLastUpdateDateRef.current =
                                _storeSettingsLastUpdateDate;
                        }
                    },
                )
                .catch((err) => {
                    logger.error(
                        "Failed to refresh global menu, store menu and / or store settings upon order completion.",
                        {
                            errorMessage: describeNetworkError(err).join("\n"),
                        },
                    );
                });
        };
    }, [isTraining]);

    const startNewOrder = () => {
        if (isDriveThruFulfillment) {
            navigate(routeHelpers.otherOrders());
        } else {
            if (defaultCategoryId) {
                navigate(routeHelpers.category(defaultCategoryId));
            } else {
                navigate(routeHelpers.menu());
            }
        }
    };

    const [printing, setPrinting] = useState(false);
    const [confirmPrinted, setConfirmPrinted] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    function onPrintReceipt() {
        if (printing || !orderId) {
            return;
        }

        setPrinting(true);
        printers
            .printReceipt(orderId, stationMode)
            .then(() => {
                setPrinting(false);
                setConfirmPrinted(true);
            })
            .catch((err) => {
                logger.warn("Failed to print receipt.", {
                    orderId,
                });
                const description = describeNetworkError(err);
                setErrorMessage(description.join("\n"));
                setPrinting(false);
            });
    }

    const defaultButtonText = isDriveThruFulfillment
        ? strings.returnToOrders
        : strings.startNewOrder;

    const showPrintReceiptButton =
        !autoPrintReceipts &&
        stationMode !== "drive_thru_order_taker" &&
        stationMode !== "line_buster";

    if (!storeId) {
        return null;
    }

    return (
        <div className={styles.container}>
            <img className={styles.icon} src={completeIcon} />

            <div
                className={classNames(
                    styles.text,
                    showPrintReceiptButton && styles.centeredContainer,
                )}>
                <div className={styles.header}>
                    {locationState?.header ??
                        strings.orderComplete(orderNumber ?? 0)}
                </div>
                {locationState?.subheader ? (
                    <div
                        className={
                            locationState?.subheaderClassName ??
                            styles.subheader
                        }>
                        {locationState?.subheader}
                    </div>
                ) : null}
                {locationState?.instructions ? (
                    <div className={styles.instructions}>
                        {locationState?.instructions}
                    </div>
                ) : null}
                {showPrintReceiptButton ? (
                    <OutlineButton
                        loading={printing}
                        className={styles.printGuestReceiptButton}
                        labelClassName={styles.printGuestReceiptLabel}
                        label={strings.printGuestReceipt}
                        mode="blue"
                        onClick={onPrintReceipt}
                    />
                ) : null}
            </div>

            <div className={styles.spacer} />

            <OutlineButton
                className={styles.button}
                label={locationState?.button ?? defaultButtonText}
                mode="blue"
                onClick={startNewOrder}
            />

            {showQsrError ? (
                <ErrorModal
                    title={strings.orderNotSentError}
                    errorMessage={strings.orderNotSentInstructions}
                    onClose={() => {
                        setShowQsrError(false);
                    }}
                    backgroundColor="grey"
                    showITInfo
                    showAlertIcon
                    orderId={orderId ?? undefined}
                />
            ) : null}
            {confirmPrinted ? (
                <TextModal
                    onClose={() => setConfirmPrinted(false)}
                    title={strings.requestSent}
                    content={strings.confirmPrinted}
                    cancelLabel={strings.ok}
                />
            ) : null}
            {errorMessage ? (
                <ErrorModal
                    errorMessage={errorMessage}
                    onClose={() => setErrorMessage("")}
                    showITInfo
                />
            ) : null}
        </div>
    );
}
